import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { applyLang, getCurrentLang } from '../utils/i18n';
import {
  seancesApi, SEANCE_TYPES, TYPE_LABEL, STATUS_LABEL,
} from '../services/seancesApi';
import './Seances.css';

const STATUS_COLOR = {
  annoncee: '#E8B53C',
  tenue:    '#1B6985',
  publiee:  '#34A853',
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
}
function fmtTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

const Seances = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');

  useEffect(() => { applyLang(getCurrentLang()); }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    seancesApi.list({
      type: filterType !== 'all' ? filterType : undefined,
      limit: 50,
    })
      .then((data) => { if (!cancelled) { setItems(data.items); setLoading(false); } })
      .catch(() => { if (!cancelled) { setItems([]); setLoading(false); } });
    return () => { cancelled = true; };
  }, [filterType]);

  const now = Date.now();

  // À venir = sessionDate dans le futur OU statut annoncee
  // Passées = sessionDate dans le passé OU statut tenue/publiee
  const upcoming = useMemo(
    () => items
      .filter((s) => new Date(s.sessionDate).getTime() >= now || s.status === 'annoncee')
      .sort((a, b) => new Date(a.sessionDate) - new Date(b.sessionDate)),
    [items, now]
  );

  const past = useMemo(
    () => items
      .filter((s) => new Date(s.sessionDate).getTime() < now && s.status !== 'annoncee')
      .sort((a, b) => new Date(b.sessionDate) - new Date(a.sessionDate)),
    [items, now]
  );

  const counts = useMemo(() => ({
    total:    items.length,
    annoncee: items.filter((s) => s.status === 'annoncee').length,
    publiee:  items.filter((s) => s.status === 'publiee').length,
  }), [items]);

  return (
    <main className="seances-page">
      {/* ============= HERO ============= */}
      <header className="sea-hero">
        <div className="sea-hero-bg" />
        <div className="sea-hero-grain" />
        <div className="sea-hero-seal" aria-hidden="true"><i>PV</i></div>

        <div className="wrap sea-hero-inner">
          <span className="sea-hero-eyebrow">Conseil municipal · Transparence</span>
          <h1>
            Les délibérations,<br />
            <span className="ital">en clair.</span>
          </h1>
          <p className="lede">
            Ordres du jour annoncés, séances tenues, procès-verbaux publiés —
            chaque décision du conseil de Cambérène en accès libre.
          </p>

          <div className="sea-hero-foot">
            <div><b>{loading ? '—' : counts.total}</b><span>Séances suivies</span></div>
            <div><b>{loading ? '—' : counts.annoncee}</b><span>À venir</span></div>
            <div><b>{loading ? '—' : counts.publiee}</b><span>PV publiés</span></div>
            <div><b>1ᵉʳ</b><span>Vendredi du mois</span></div>
          </div>
        </div>
      </header>

      {/* ============= FILTRE ============= */}
      <section className="sea-section">
        <div className="wrap">
          <div className="sea-filter-bar">
            {SEANCE_TYPES.map((t) => (
              <button
                key={t.id}
                className={`sea-pill ${filterType === t.id ? 'on' : ''}`}
                onClick={() => setFilterType(t.id)}
              >{t.lbl}</button>
            ))}
          </div>

          {loading && <p className="sea-muted">Chargement…</p>}

          {/* ============= À VENIR ============= */}
          {!loading && upcoming.length > 0 && (
            <>
              <div className="sea-head">
                <span className="eyebrow">Prochaines séances</span>
                <h2>À venir<span className="ital"> · {upcoming.length}</span></h2>
              </div>
              <div className="sea-grid">
                {upcoming.map((s) => <SeanceCard key={s.id} s={s} />)}
              </div>
            </>
          )}

          {/* ============= ARCHIVES ============= */}
          {!loading && past.length > 0 && (
            <>
              <div className="sea-head" style={{ marginTop: 56 }}>
                <span className="eyebrow">Archives</span>
                <h2>Séances passées<span className="ital"> · {past.length}</span></h2>
              </div>
              <div className="sea-grid">
                {past.map((s) => <SeanceCard key={s.id} s={s} />)}
              </div>
            </>
          )}

          {!loading && items.length === 0 && (
            <p className="sea-muted" style={{ textAlign: 'center', padding: '60px 0' }}>
              Aucune séance enregistrée pour le moment.
            </p>
          )}
        </div>
      </section>
    </main>
  );
};

const SeanceCard = ({ s }) => (
  <Link to={`/seances/${s.id}`} className="sea-card">
    <div className="sea-card-head">
      <span className="sea-ref">{s.reference || '—'}</span>
      <span className="sea-status" style={{ background: STATUS_COLOR[s.status] || 'var(--char-500)' }}>
        {STATUS_LABEL[s.status] || s.status}
      </span>
    </div>
    <h3>{s.title}</h3>
    <div className="sea-meta">
      <span>{fmtDate(s.sessionDate)}</span>
      <i />
      <span>{fmtTime(s.sessionDate)}</span>
      {s.location && (<><i /><span>📍 {s.location}</span></>)}
    </div>
    {s.summary && <p className="sea-summary">{s.summary}</p>}
    <div className="sea-card-foot">
      <span>{s.agenda?.length || 0} point{(s.agenda?.length || 0) > 1 ? 's' : ''} à l'ordre du jour</span>
      {s.status === 'publiee' && s.decisions?.length > 0 && (
        <span>· {s.decisions.length} décision{s.decisions.length > 1 ? 's' : ''}</span>
      )}
      <span className="sea-arrow">→</span>
    </div>
  </Link>
);

export default Seances;
